// Configure ActionCable to use the correct WebSocket URL
import { createConsumer } from "@rails/actioncable"
import Logger from './lib/logger'

// Use the WebSocket URL from the meta tag or fall back to the default
const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
const host = window.location.host
const url = document.head.querySelector('meta[name="action-cable-url"]')?.content || 
            `${protocol}//${host}/cable`

Logger.debug('ActionCable: Creating consumer', { url })

// Create consumer with enhanced connection options
const consumer = createConsumer(url)

// Make ActionCable and consumer available globally for compatibility
window.ActionCable = { createConsumer }
window.App = window.App || {}
window.App.cable = consumer

Logger.debug('ActionCable: Consumer set as App.cable')

// Add connection event handlers for better debugging and recovery
// Note: ActionCable handles reconnection automatically, but we can monitor the connection
consumer.connection.onopen = () => {
  Logger.debug('ActionCable WebSocket connection opened')
  Logger.debug('Connection state', { state: consumer.connection.getState() })
  Logger.debug('Connection URL', { url: consumer.connection.getURL() })
  
  // Start keep-alive ping immediately when connection opens
  startKeepAlive()
}

consumer.connection.onclose = (event) => {
  Logger.debug('ActionCable WebSocket connection closed', { code: event.code, reason: event.reason })
  Logger.debug('Close event details', {
    code: event.code,
    reason: event.reason,
    wasClean: event.wasClean,
    timestamp: new Date().toISOString()
  })
  
  // Stop keep-alive when connection closes
  stopKeepAlive()
  
  // ActionCable will automatically attempt to reconnect
  Logger.debug('ActionCable will attempt to reconnect automatically')
}

consumer.connection.onerror = (error) => {
  Logger.error('ActionCable WebSocket connection error', error)
  Logger.error('Error details', {
    error: error,
    timestamp: new Date().toISOString()
  })
}

// Keep-alive ping management
let keepAliveInterval = null
let pingCount = 0

function startKeepAlive() {
  // Clear any existing interval
  if (keepAliveInterval) {
    clearInterval(keepAliveInterval)
  }
  
  Logger.debug('Starting keep-alive ping', { interval: '15 seconds' })
  
  // Send ping every 15 seconds to prevent suspension
  keepAliveInterval = setInterval(() => {
    if (consumer.connection.isOpen && consumer.connection.isOpen()) {
      try {
        pingCount++
        Logger.debug('Sending keep-alive ping', { count: pingCount })
        consumer.connection.send({ type: 'ping', count: pingCount, timestamp: new Date().toISOString() })
      } catch (e) {
        Logger.debug('Could not send ping', { message: e.message })
        // If ping fails, try to reconnect
        if (consumer.connection.reopen) {
          Logger.debug('Attempting to reconnect due to ping failure')
          consumer.connection.reopen()
        }
      }
    } else {
      Logger.debug('Connection not open, stopping keep-alive')
      stopKeepAlive()
    }
  }, 15000) // Send ping every 15 seconds (more frequent to prevent suspension)
}

function stopKeepAlive() {
  if (keepAliveInterval) {
    clearInterval(keepAliveInterval)
    keepAliveInterval = null
    Logger.debug('Keep-alive ping stopped')
  }
}

// Start keep-alive if connection is already open
if (consumer.connection.isOpen && consumer.connection.isOpen()) {
  Logger.debug('Connection already open, starting keep-alive')
  startKeepAlive()
}

// Add periodic connection health check
setInterval(() => {
  if (consumer.connection.isOpen && consumer.connection.isOpen()) {
    Logger.debug('Connection health check: OK')
  } else {
    Logger.debug('Connection health check: Connection not open')
  }
}, 30000) // Check every 30 seconds

export default consumer
